"use client";

import { motion, useReducedMotion } from "framer-motion";

/** Widths of the caption lines under the amount, shortest last, like the real captions. */
const CAPTION_WIDTHS = ["w-24", "w-12"] as const;

/**
 * Shimmering bars standing in for an amount that is still loading, sized like what replaces
 * them: one amount line, plus the caption lines under it (a fare's flight and View link, a
 * hotel's venue count), so the row doesn't grow when the value lands.
 * @param props.width - The amount bar's width class, default `w-16`.
 * @param props.captions - How many caption lines the real content has under the amount.
 * @returns The skeleton.
 * @throws Never.
 */
export const CellSkeleton = ({ width = "w-16", captions = 0 }: { width?: string; captions?: 0 | 1 | 2 }) => {
  const reduceMotion = useReducedMotion();
  const bar = (className: string) => (
    <span className={`relative block overflow-hidden rounded-md bg-gray-100 ${className}`}>
      {!reduceMotion && (
        <motion.span
          className="absolute inset-y-0 w-2/3 bg-linear-to-r from-transparent via-white/50 to-transparent"
          initial={{ x: "-100%" }}
          animate={{ x: "160%" }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.6, ease: "easeInOut" }}
        />
      )}
    </span>
  );
  return (
    <span role="status" aria-label="Loading" className="ml-auto flex flex-col items-end gap-1">
      {/* Amount lines are 20px tall, captions 16px: bars sit inside boxes of those heights. */}
      <span className="flex h-5 items-center">{bar(`h-4 ${width}`)}</span>
      {CAPTION_WIDTHS.slice(0, captions).map((caption) => (
        <span key={caption} className="flex h-4 items-center">
          {bar(`h-3 ${caption}`)}
        </span>
      ))}
    </span>
  );
};
